import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  CircularLoader,
  NoticeBox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  InputField
} from '@dhis2/ui';
import * as XLSX from 'xlsx';
import { POSITION_ATTRIBUTES, TEMPLATE_STRUCTURE } from '../api/constants';
import { useDataEngine } from '@dhis2/app-runtime';

const BulkPositionImportDialog = ({ 
  open, 
  onClose, 
  onImportComplete,
  departments = [],
  staffOptions = [],
  positionsList = [],
  userOrgUnit = null
}) => {
  const [importData, setImportData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importStatus, setImportStatus] = useState({
    loading: false,
    processed: 0,
    total: 0,
    success: 0,
    failed: 0
  });
  const [activeStep, setActiveStep] = useState(1);
  const [duplicateCheck, setDuplicateCheck] = useState({
    duplicateTitles: new Set(),
    duplicateIds: new Set()
  });
 
  const [duplicateHandling, setDuplicateHandling] = useState('skip'); // 'skip' | 'update' | 'abort'
  const [duplicatePositions, setDuplicatePositions] = useState([]);
  const [rootOrgUnit, setRootOrgUnit] = useState(null);
  
  const fileInputRef = useRef(null);
  const engine = useDataEngine();

  const showNotification = (message, type = 'error') => {
  // You can dispatch to parent, log, or just ignore for now
  console.warn(`[BulkImport Notification] ${type}:`, message);
  // Or if your parent expects a callback:
  if (onImportComplete && type === 'error') {
    onImportComplete({ success: 0, failed: 1, error: message });
  }
};
  

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportStatus({ ...importStatus, loading: true });

    try {
      const data = await readExcelFile(file);
      const validatedData = await validateAndProcessData(data);
      setImportData(validatedData.processed);
      setValidationErrors(validatedData.errors);
      setActiveStep(2);
    } catch (error) {
      console.error('Error processing file:', error);
      setValidationErrors([{
        row: 0,
        message: `Error reading file: ${error.message}`,
        field: 'File'
      }]);
    } finally {
      setImportStatus({ ...importStatus, loading: false });
    }
  };

  // Read and parse Excel/CSV file
  const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          raw: false
        });
        
        if (jsonData.length === 0) {
          reject(new Error('File is empty'));
          return;
        }
        
        // Get headers from first row
        const headers = jsonData[0].map(h => h.trim());
        
        console.log('📄 File headers:', headers);
        
        // Validate headers
        const expectedHeaders = [
          'Position Title',
          'Job Grade',
          'Status',
          'Reporting To Position Title'
        ];
        
        // Check if all expected headers are present
        const missingHeaders = expectedHeaders.filter(
          header => !headers.includes(header)
        );
        
        if (missingHeaders.length > 0) {
          reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
          return;
        }
        
        // Process data rows
        const structuredData = jsonData.slice(1).map((row, index) => {
          const obj = {};
          headers.forEach((header, colIndex) => {
            if (header && row[colIndex] !== undefined) {
              obj[header] = String(row[colIndex]).trim();
            } else {
              obj[header] = '';
            }
          });
          return {
            rawData: obj,
            rowNumber: index + 2
          };
        }).filter(item => {
          // Keep rows that have at least one required field filled
          const hasRequiredField = TEMPLATE_STRUCTURE.requiredFields.some(
            field => item.rawData[field] && item.rawData[field].trim() !== ''
          );
          return hasRequiredField;
        });
        
        console.log('📊 Processed data:', structuredData.length, 'rows');
        resolve(structuredData);
        
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

const validateAndProcessData = async (data) => {
  const errors = [];
  const processed = [];
  const duplicates = []; 
  const validPositions = [];
  const duplicateTitles = new Set();
  const duplicateIds = new Set();
  const timestamp = Date.now().toString().slice(-6);

  // FIRST: Build a Set of all position titles in this batch
  const batchTitles = new Set();
  for (let i = 0; i < data.length; i++) {
    const { rawData } = data[i];
    if (rawData['Position Title']) {
      batchTitles.add(rawData['Position Title'].trim());
    }
  }
  
  // Create Position ID to TEI ID mapping
  const positionIdToTeiMap = {};
  // Create Position Title to TEI ID mapping
  const positionTitleToTeiMap = {};
  
  // Also map title → full position for duplication checking
  const existingPositionsByTitle = {};
  const existingPositionsById = {};

  positionsList.forEach(position => {
    const attributes = position.attributes || [];
    
    // Map Position ID → TEI ID
    const positionIdAttr = attributes.find(attr => 
      attr.attribute === 'VwhhrV1ODQK' // Position ID attribute
    );
    
    // Map Position Title → TEI ID
    const titleAttr = attributes.find(attr => 
      attr.attribute === 'NWe61Sa41CS' // Position Title attribute
    );
    
    if (positionIdAttr && positionIdAttr.value) {
      const pid = positionIdAttr.value;
      positionIdToTeiMap[pid] = position.trackedEntityInstance;
      existingPositionsById[pid] = position;
    }
    
    if (titleAttr && titleAttr.value) {
      const title = titleAttr.value.trim();
      positionTitleToTeiMap[title] = position.trackedEntityInstance;
      existingPositionsByTitle[title] = position;
    }
  });

  // ✅ DETERMINE ORG UNIT FOR ALL POSITIONS
  let orgUnitId = null;
  let orgUnitName = 'Default Organisation';
  
  // Priority 1: Use rootOrgUnit from state
  if (rootOrgUnit?.id) {
    orgUnitId = rootOrgUnit.id;
    orgUnitName = rootOrgUnit.name;
    console.log('✅ Using rootOrgUnit for bulk import:', orgUnitId, orgUnitName);
  }
  // Priority 2: Use userOrgUnit from props
  else if (userOrgUnit?.id) {
    orgUnitId = userOrgUnit.id;
    orgUnitName = userOrgUnit.name;
    console.log('✅ Using userOrgUnit from props:', orgUnitId, orgUnitName);
  }
  // Priority 3: Try to get from current user (fallback)
  else {
    try {
      const { me } = await engine.query({
        me: {
          resource: 'me',
          params: { fields: 'organisationUnits[id,name]' }
        }
      });
      if (me.organisationUnits?.[0]?.id) {
        orgUnitId = me.organisationUnits[0].id;
        orgUnitName = me.organisationUnits[0].name;
        console.log('✅ Using current user org unit:', orgUnitId, orgUnitName);
      }
    } catch (error) {
      console.warn('⚠️ Could not get org unit from current user:', error);
    }
  }

  if (!orgUnitId) {
    // Add a single error for all rows
    errors.push({
      rowNumber: 0,
      errors: [{
        field: 'Organisation Unit',
        message: 'Could not determine organisation unit. Please try again or contact support.'
      }],
      data: {}
    });
    return { processed: [], errors, duplicates: [] };
  }

  for (let i = 0; i < data.length; i++) {
    const { rawData, rowNumber } = data[i];
    const rowErrors = [];

    // Check required fields
    const requiredFields = ['Position Title', 'Job Grade', 'Status'];
    requiredFields.forEach(field => {
      if (!rawData[field] || rawData[field].trim() === '') {
        rowErrors.push({
          field,
          message: `${field} is required`
        });
      }
    });

    // Validate status
    if (rawData.Status && !['Active', 'Vacant', 'Inactive'].includes(rawData.Status)) {
      rowErrors.push({
        field: 'Status',
        message: 'Status must be Active, Vacant, or Inactive'
      });
    }

    // Handle Job Grade
    let jobGradeValue = rawData['Job Grade'] || '';
    const reportingToTitle = rawData['Reporting To Position Title'] || '';

    // Helper: generate a random numeric string of given length
    const generateNumericSuffix = (length = 7) => {
      let result = '';
      const digits = '0123456789';
      for (let i = 0; i < length; i++) {
        result += digits.charAt(Math.floor(Math.random() * digits.length));
      }
      return result;
    };
    
    // Generate Position ID
    let positionId = '';
    if (rawData['Position Title']) {
      const cleanTitle = rawData['Position Title']
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 3);
      
      const uniqueSuffix = generateNumericSuffix(7);
      positionId = `POS-${cleanTitle}-${uniqueSuffix}`;
      
      // Check for duplicates in batch
      if (duplicateIds.has(positionId)) {
        rowErrors.push({
          field: 'Position ID',
          message: 'Duplicate Position ID generated in this batch'
        });
      } else {
        duplicateIds.add(positionId);
      }
    }

    // Check for duplicate titles
    const titleKey = `${rawData['Position Title']}`.toLowerCase();
    if (duplicateTitles.has(titleKey)) {
      rowErrors.push({
        field: 'Position Title',
        message: 'Duplicate position title in this batch'
      });
    } else {
      duplicateTitles.add(titleKey);
    }

    // 🔍 Check for EXISTING position (by Position ID or Title)
    let isDuplicate = false;
    let duplicateTei = null;
    let duplicateSource = null; // 'id' or 'title'

    if (positionId && existingPositionsById[positionId]) {
      isDuplicate = true;
      duplicateTei = existingPositionsById[positionId].trackedEntityInstance;
      duplicateSource = 'id';
    } else if (rawData['Position Title'] && existingPositionsByTitle[rawData['Position Title'].trim()]) {
      isDuplicate = true;
      duplicateTei = existingPositionsByTitle[rawData['Position Title'].trim()].trackedEntityInstance;
      duplicateSource = 'title';
    }

    // Validate Reporting To Position Title
    let reportingToTei = '';
    if (rawData['Reporting To Position Title'] && rawData['Reporting To Position Title'].trim() !== '') {
      const reportingTitle = rawData['Reporting To Position Title'].trim();
      
      if (reportingTitle === '') {
        reportingToTei = '';
      } 
      else if (positionTitleToTeiMap[reportingTitle]) {
        reportingToTei = positionTitleToTeiMap[reportingTitle];
      } 
      else if (batchTitles.has(reportingTitle)) {
        console.log(`✅ Position "${rawData['Position Title']}" reports to "${reportingTitle}" which is in this batch`);
        reportingToTei = '';
      }
      else if (positionIdToTeiMap[reportingTitle]) {
        reportingToTei = positionIdToTeiMap[reportingTitle];
      } 
      else {
        const existingTitles = Object.keys(positionTitleToTeiMap).slice(0, 5);
        rowErrors.push({
          field: 'Reporting To Position Title',
          message: `Position with title "${reportingTitle}" not found. Existing titles: ${existingTitles.join(', ')}`
        });
      }
    }

    if (rowErrors.length === 0) {
      const positionRecord = {
        ...rawData,
        positionId,
        reportingToTei,
        reportingToTitle,
        jobGradeValue,
        status: rawData.Status || 'Active',
        // ✅ ADDED: Org unit information
        departmentId: orgUnitId, // This will be used in savePosition
        orgUnitName: orgUnitName,
        needsReportingUpdate: batchTitles.has(rawData['Reporting To Position Title']?.trim()),
        reportingTitle: rawData['Reporting To Position Title']?.trim(),
        
        // Mapped attributes for backend database
        mappedAttributes: {
          'VwhhrV1ODQK': positionId,
          'NWe61Sa41CS': rawData['Position Title'],
          'YsGPrrVLAuM': orgUnitName, // Store org unit name as "department"
          'gp4AnggIDi8': jobGradeValue,
          'Ch7TyL91heU': rawData.Status || 'Active',
          'SjP47qi3JeB': reportingToTei || '',
          'V66IFsw5kq6': '',
          'YFmL9BJVJ44': '',
          'g3RRIfDiQra': ''
        },
        
        rowNumber,
        isValid: true
      };

      if (isDuplicate) {
        // PUSH TO DUPLICATES instead of processed
        duplicates.push({
          ...positionRecord,
          duplicateTei,
          duplicateSource,
          originalPosition: duplicateSource === 'id' 
            ? existingPositionsById[positionId] 
            : existingPositionsByTitle[rawData['Position Title'].trim()]
        });
      } else {
        processed.push(positionRecord);
      }
    } else {
      errors.push({
        rowNumber,
        errors: rowErrors,
        data: rawData
      });
    }
  }

  setDuplicateCheck({
    duplicateTitles,
    duplicateIds
  });

  // ✅ Return duplicates so UI can show notification
  return { processed, errors, duplicates };
};



// Add this to your component or a utility file
const discoverJobGradeMappings = () => {
  const jobGradeMap = {};
  
  positionsList.forEach(position => {
    const attributes = position.attributes || [];
    const jobGradeAttr = attributes.find(attr => 
      attr.attribute === POSITION_ATTRIBUTES.JOB_GRADE
    );
    const titleAttr = attributes.find(attr => 
      attr.attribute === POSITION_ATTRIBUTES.POSITION_TITLE
    );
    
    if (jobGradeAttr && jobGradeAttr.value && titleAttr && titleAttr.value) {
      // Try to extract readable grade from title
      const title = titleAttr.value;
      const gradeUid = jobGradeAttr.value;
      
      // Common patterns to extract grade from title
      const gradePatterns = [
        /(Senior|Sr\.?)\s+(\w+)/i,
        /(\w+)\s+(Manager|Mgr)/i,
        /(\w+)\s+(Director|Dir)/i,
        /(\w+)\s+(Analyst)/i,
        /(\w+)\s+(Executive|Exec)/i,
        /(VP|Vice President)/i,
        /(C[-\s]?Level|Chief)/i
      ];
      
      let readableGrade = '';
      for (const pattern of gradePatterns) {
        const match = title.match(pattern);
        if (match) {
          readableGrade = match[0];
          break;
        }
      }
      
      if (readableGrade && !jobGradeMap[readableGrade]) {
        jobGradeMap[readableGrade] = gradeUid;
        console.log(`Discovered mapping: "${readableGrade}" → ${gradeUid}`);
      }
    }
  });
  
  return jobGradeMap;
};

// Load root org unit for bulk import
useEffect(() => {
  const loadRootOrgUnit = async () => {
    if (!open) return;
    
    console.log('🔄 Loading root org unit for bulk import...');
    
    try {
      // Get current user
      const { me } = await engine.query({
        me: {
          resource: 'me',
          params: { fields: 'organisationUnits[id,name,path]' }
        }
      });
      
      if (!me.organisationUnits || me.organisationUnits.length === 0) {
        console.error('❌ User has no organisation units');
        return;
      }
      
      // Simple: Use the first org unit as root
      const userOrgUnit = me.organisationUnits[0];
      
      setRootOrgUnit(userOrgUnit);
      console.log('✅ Root org unit for bulk import:', userOrgUnit.name, userOrgUnit.id);
      
    } catch (error) {
      console.error('❌ Failed to load root org unit for bulk import:', error);
    }
  };
  
  if (open) {
    loadRootOrgUnit();
  }
}, [open, engine]);

// Use in your component
useEffect(() => {
  if (positionsList.length > 0) {
    const discoveredGrades = discoverJobGradeMappings();
    console.log('Discovered job grade mappings:', discoveredGrades);
    // You can store this in state or use it directly
  }
}, [positionsList]);

  // Download template - UPDATED
    const downloadTemplate = () => {
      const headers = [
        'Position Title',
        'Job Grade',
        'Status',
        'Reporting To Position Title',
      ];
      
      // Get existing position titles for examples
      const existingPositions = positionsList.slice(0, 5).map(pos => {
        const attrs = pos.attributes || [];
        const title = attrs.find(a => a.attribute === POSITION_ATTRIBUTES.POSITION_TITLE)?.value || 'Untitled';
        return title;
      });

      const exampleData = [
        {
          'Position Title': 'Human Resources Manager',
          'Job Grade': 'Senior Manager',
          'Status': 'Vacant',
          'Reporting To Position Title': 'Chief Executive Officer',
        },
        {
          'Position Title': 'Finance Analyst',
          'Job Grade': 'Analyst',
          'Status': 'Vacant',
          'Reporting To Position Title': 'Human Resources Manager', // No reporting position
          }
      ];

      // Add instructions sheet
      const instructionData = [
        ['=== BULK POSITION IMPORT TEMPLATE ==='],
        [''],
        ['INSTRUCTIONS:'],
        ['2. Status must be: Active, Vacant, or Inactive'],
        ['3. Job Grade can be:'],
        ['   - Readable name (like "Senior Manager")'],
        ['4. Reporting To Position Title should be an existing position title'],
        ['5. Current Incumbent is NOT set during bulk import'],
        ['6. Position IDs are auto-generated'],
        [''],
        ['=== EXISTING POSITION TITLES ==='],
        ['(For Reporting To field)'],
        ...existingPositions.map(title => [title]),
        [''],
        ['=== EXAMPLE JOB GRADES ==='],
        ['Senior Manager', 'Manager', 'Analyst', 'Senior Staff', 'Director', 'VP', 'C-Level'],
        [''],
        ['=== SAMPLE POSITION IDs ==='],
        ['(Auto-generated examples)'],
        ['HUM-HRM-abc123de', 'IT-SWE-def456gh', 'FIN-ANA-ghi789jk']
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Data sheet
      const wsData = XLSX.utils.json_to_sheet(exampleData, { header: headers });
      XLSX.utils.book_append_sheet(wb, wsData, 'Template');
      
      // Instructions sheet
      const wsInstructions = XLSX.utils.aoa_to_sheet(instructionData);
      
      // Auto-size columns for instructions
      const maxColLengths = instructionData.reduce((acc, row) => {
        row.forEach((cell, colIndex) => {
          acc[colIndex] = Math.max(acc[colIndex] || 0, String(cell).length);
        });
        return acc;
      }, {});
      
      wsInstructions['!cols'] = Object.keys(maxColLengths).map(colIndex => ({
        wch: Math.min(maxColLengths[colIndex] + 2, 80)
      }));
      
      XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
      
      XLSX.writeFile(wb, 'Position_Import_Template.xlsx');
    };
    
   
// Start import process
const handleStartImport = async () => {
  if (importData.length === 0) return;

  setImportStatus({
    loading: true,
    processed: 0,
    total: importData.length,
    success: 0,
    failed: 0
  });
  setActiveStep(3);

  const results = [];
  const allCreatedPositions = []; // ✅ Define it here

  try {
    // Step 1: Create all positions
    for (let i = 0; i < importData.length; i++) {
      const positionData = importData[i];
      let saveResult;

      try {
        if (positionData.needsReportingUpdate) {
          // Temporarily remove reporting attribute
          const tempAttributes = { ...positionData.mappedAttributes };
          delete tempAttributes['SjP47qi3JeB'];
          const tempPositionData = {
            ...positionData,
            mappedAttributes: tempAttributes
          };
          saveResult = await savePosition(tempPositionData);
        } else {
          saveResult = await savePosition(positionData);
        }

        // ✅ Collect created position for post-processing
        if (saveResult && saveResult.teiId) {
          allCreatedPositions.push({
            teiId: saveResult.teiId,
            positionId: saveResult.positionId,
            positionTitle: saveResult.positionTitle,
            originalData: positionData,
            needsReportingUpdate: positionData.needsReportingUpdate,
            orgUnit: saveResult.orgUnit
          });
        }

        results.push({ success: true, data: saveResult });
        setImportStatus(prev => ({
          ...prev,
          processed: i + 1,
          success: prev.success + 1
        }));
      } catch (error) {
        console.error(`❌ Failed to import position "${positionData['Position Title']}":`, error.message);
        results.push({ 
          success: false, 
          error: error.message,
          rowData: positionData
        });
        setImportStatus(prev => ({
          ...prev,
          processed: i + 1,
          failed: prev.failed + 1
        }));
      }

      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    console.log('✅ All positions created. Total:', allCreatedPositions.length);
    console.log('📋 Created positions:', allCreatedPositions);

    // Step 2: Update reporting relationships (for positions that report to others in the batch)
    if (allCreatedPositions.length > 0 && allCreatedPositions.some(p => p.needsReportingUpdate)) {
      console.log('🔄 Updating reporting relationships...');
      
      // Build title → TEI map from newly created positions
      const titleToTeiMap = {};
      allCreatedPositions.forEach(pos => {
        if (pos.positionTitle) {
          titleToTeiMap[pos.positionTitle] = pos.teiId;
        }
      });

      console.log('🗺️ Title to TEI map:', titleToTeiMap);

      // Update positions that need reporting
      for (const position of allCreatedPositions) {
        if (position.needsReportingUpdate && position.originalData?.reportingTitle) {
          try {
            const reportingTeiId = titleToTeiMap[position.originalData.reportingTitle];
            if (reportingTeiId) {
              // Build full attributes from original data
              const fullAttributes = Object.entries(position.originalData.mappedAttributes || {})
                .map(([attribute, value]) => ({
                  attribute,
                  value: String(value || '')
                }));

              await updateReportingAttribute(
                position.teiId,
                reportingTeiId,
                fullAttributes,
                position.orgUnit || position.originalData.departmentId,
                'UGGo0Ebjl0E'
              );
              console.log(`✅ Updated reporting for ${position.positionTitle}`);
            } else {
              console.warn(`⚠️ Could not find reporting TEI for title: ${position.originalData.reportingTitle}`);
            }
          } catch (error) {
            console.error(`❌ Failed to update reporting for ${position.positionTitle}:`, error);
            // Don't fail the whole import for reporting errors
          }
        }
      }
    }

    setImportStatus(prev => ({ ...prev, loading: false }));
    
    // Trigger refresh in parent
    if (onImportComplete) {
      onImportComplete({
        success: importStatus.success + results.filter(r => r.success).length,
        failed: importStatus.failed + results.filter(r => !r.success).length,
        results,
        total: importData.length
      });
    }
  } catch (error) {
    console.error('💥 Unexpected error during bulk import:', error);
    setImportStatus(prev => ({ ...prev, loading: false }));
    showNotification('Bulk import failed due to an unexpected error', 'error');
  }
};

// Add this helper function to update reporting attribute
const updateReportingAttribute = async (teiId, reportingTeiId, originalAttributes, orgUnit, trackedEntityType) => {
  // Build full attribute list with updated reporting
  const updatedAttributes = originalAttributes.map(attr =>
    attr.attribute === 'SjP47qi3JeB'
      ? { ...attr, value: reportingTeiId }
      : attr
  );

  // Ensure reporting attribute exists
  const hasReporting = updatedAttributes.some(a => a.attribute === 'SjP47qi3JeB');
  if (!hasReporting) {
    updatedAttributes.push({
      attribute: 'SjP47qi3JeB',
      value: reportingTeiId
    });
  }

  try {
    const mutation = {
      type: 'update',
      resource: 'trackedEntityInstances',
      id: teiId,
      data: {
        trackedEntityType, 
        orgUnit: orgUnit || rootOrgUnit?.id || userOrgUnit?.id, // Handle missing orgUnit
        attributes: updatedAttributes
      }
    };
    await engine.mutate(mutation);
    console.log(`✅ Updated reporting for TEI ${teiId}`);
  } catch (error) {
    console.error('❌ Error updating reporting attribute:', error);
    throw error;
  }
};



const savePosition = async (positionData) => {
  try {
    console.log('💾 Saving position:', {
      title: positionData['Position Title'],
      mappedAttributes: positionData.mappedAttributes
    });

    // ✅ FIXED: Determine orgUnit for bulk import
    let positionOrgUnit = null;
    
    // Priority 1: Try to get orgUnit from positionData (if passed)
    if (positionData.departmentId) {
      positionOrgUnit = positionData.departmentId;
      console.log('📌 Using departmentId from positionData:', positionOrgUnit);
    }
    // Priority 2: Use root org unit
    else if (rootOrgUnit?.id) {
      positionOrgUnit = rootOrgUnit.id;
      console.log('📌 Using root org unit:', positionOrgUnit, rootOrgUnit.name);
    }
    // Priority 3: Try to get from userOrgUnit prop (if passed from parent)
    else if (userOrgUnit?.id) {
      positionOrgUnit = userOrgUnit.id;
      console.log('📌 Using userOrgUnit from props:', positionOrgUnit);
    }
    
    if (!positionOrgUnit) {
      console.error('❌ No orgUnit found for position:', positionData);
      throw new Error(`Could not determine organisation unit for position "${positionData['Position Title']}".`);
    }

    // Build attributes array
    const attributes = Object.entries(positionData.mappedAttributes || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([attribute, value]) => ({
        attribute,
        value: String(value || '')
      }));

    console.log('📤 Sending to database with orgUnit:', positionOrgUnit);

    const mutation = {
      type: 'create',
      resource: 'trackedEntityInstances',
      data: {
        trackedEntityType: 'UGGo0Ebjl0E',
        orgUnit: positionOrgUnit,
        attributes: attributes
      }
    };

    const response = await engine.mutate(mutation);
    const teiId = response.response?.importSummaries?.[0]?.reference || '';
    const positionId = positionData.positionId || '';
    const positionTitle = positionData['Position Title'] || '';

    console.log(`✅ Created position: ${positionId} (${positionTitle}) - TEI ID: ${teiId}`);

    // ✅ RETURN the expected shape for handleStartImport
    return {
      teiId,
      positionId,
      positionTitle,
      orgUnit: positionOrgUnit, // Include orgUnit for reference
      response // include full response for debugging
    };
  } catch (error) {
    console.error(`❌ Error creating position "${positionData['Position Title']}":`, {
      error: error.message,
      positionData
    });
    throw error; // re-throw so caller can catch
  }
};


  // Reset dialog
  const handleReset = () => {
    setImportData([]);
    setValidationErrors([]);
    setImportStatus({
      loading: false,
      processed: 0,
      total: 0,
      success: 0,
      failed: 0
    });
    setActiveStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get department names for preview
  const getDepartmentNames = () => {
    return departments.slice(0, 10).map(d => d.name).join(', ');
  };

  return (
    <Modal open={open} onClose={onClose} large>
      <ModalTitle>Bulk Position Import</ModalTitle>
      
      <ModalContent>
        {/* Step Indicator */}
        <div style={{ 
          display: 'flex', 
          marginBottom: '24px',
          justifyContent: 'center',
          gap: '8px'
        }}>
          {[1, 2, 3].map(step => (
            <div
              key={step}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: activeStep >= step ? 1 : 0.5
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: activeStep === step ? '#2C6693' : '#e0e0e0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {step}
              </div>
              <span style={{ fontSize: '12px' }}>
                {step === 1 && 'Upload'}
                {step === 2 && 'Validate'}
                {step === 3 && 'Import'}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {activeStep === 1 && (
          <div style={{ padding: '20px' }}>
            <NoticeBox title="Bulk Position Import Instructions" style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                <p><strong>Important Notes:</strong></p>
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>✅ <strong>No department selection needed</strong></li>
                  <li>✅ <strong>All positions</strong> will be created under: <strong>{rootOrgUnit?.name || userOrgUnit?.name || 'Default Organisation'}</strong></li>
                  <li>✅ <strong>Current Incumbent is NOT included</strong> in bulk import</li>
                  <li>✅ <strong>Reporting To Position Title</strong> should be an existing position title</li>
                  <li>✅ <strong>Position IDs</strong> will be auto-generated based on title</li>
                </ul>
              </div>
            </NoticeBox>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              marginBottom: '20px',
              gap: '10px',
              marginTop: '10px'
            }}>
              <button
               style={{
                    backgroundColor: 'rgb(28, 171, 6)',
                    color: '#fff',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                    cursor: 'pointer',
                    }}
               onClick={downloadTemplate}>
                Download Template (.xlsx)
              </button>
             </div>
            
            <div style={{ 
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '40px',
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              
              {importStatus.loading ? (
                <div>
                  <CircularLoader />
                  <p style={{ marginTop: '10px' }}>Processing file...</p>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                    <strong>Upload your filled template</strong>
                  </p>
                  <Button 
                    onClick={() => fileInputRef.current.click()}
                    primary
                    large
                  >
                    Browse Files
                  </Button>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Supports .xlsx, .xls, .csv files (max 1000 rows)
                  </p>
                  <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    Download the template first, fill it, then upload here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Validate */}
        {activeStep === 2 && (
          <div>
            <NoticeBox 
              title={`Validation Results: ${importData.length} valid, ${validationErrors.length} with errors`}
              style={{ marginBottom: '20px' }}
              valid={validationErrors.length === 0}
              warning={validationErrors.length > 0}
            >
              {validationErrors.length === 0 ? (
                'All data is valid. Ready to import.'
              ) : (
                'Please fix errors in your file and re-upload, or proceed with valid positions only.'
              )}
            </NoticeBox>
            
            {validationErrors.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ color: '#d32f2f' }}>
                    Errors Found ({validationErrors.length} rows)
                  </h4>
                  <Button 
                    small 
                    onClick={() => {
                      // Export errors to CSV
                      const errorCSV = validationErrors.map(err => ({
                        'Row': err.rowNumber,
                        'Position Title': err.data['Position Title'] || '',
                        'Department': err.data.Department || '',
                        'Error Field': err.errors.map(e => e.field).join('; '),
                        'Error Message': err.errors.map(e => e.message).join('; ')
                      }));
                      
                      const ws = XLSX.utils.json_to_sheet(errorCSV);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Errors');
                      XLSX.writeFile(wb, 'Import_Errors.xlsx');
                    }}
                  >
                    Export Errors
                  </Button>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Position Title</TableCell>
                        <TableCell>Errors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationErrors.slice(0, 50).map((error, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{error.rowNumber}</TableCell>
                          <TableCell>{error.data['Position Title'] || 'N/A'}</TableCell>                          
                          <TableCell style={{ color: '#d32f2f', fontSize: '12px' }}>
                            {error.errors.map((e, i) => (
                              <div key={i} style={{ marginBottom: '4px' }}>
                                <strong>{e.field}:</strong> {e.message}
                              </div>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {validationErrors.length > 50 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Showing first 50 of {validationErrors.length} errors
                  </p>
                )}
              </div>
            )}
            
            {importData.length > 0 && (
              <div>
                <h4>Valid Positions ({importData.length}):</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Position ID</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Reporting To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importData.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <code style={{ fontSize: '11px' }}>{item.positionId}</code>
                          </TableCell>
                          <TableCell>{item['Position Title']}</TableCell>                          
                          <TableCell>
                            <Chip 
                              dense 
                              color={
                                item.Status === 'Active' ? 'primary' :
                                item.Status === 'Vacant' ? 'warning' : 'default'
                              }
                            >
                              {item.Status}
                            </Chip>
                          </TableCell>
                          <TableCell>
                            {item['Reporting To Position Title'] && item['Reporting To Position Title'].trim() !== '' ? (
                              <span style={{ fontSize: '11px', color: '#2C6693' }}>
                                {item['Reporting To Position Title']}
                              </span>
                            ) : (
                              <span style={{ color: '#999', fontSize: '11px' }}>None</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {importData.length > 10 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Showing first 10 of {importData.length} positions
                  </p>
                )}
                
                {/* Summary stats */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: '10px',
                  marginTop: '20px',
                  fontSize: '12px'
                }}>
                  <div style={{ 
                    background: '#e8f5e9', 
                    padding: '10px', 
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{importData.length}</div>
                    <div>Total Positions</div>
                  </div>
                  <div style={{ 
                    background: '#e3f2fd', 
                    padding: '10px', 
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {importData.filter(p => p.Status === 'Active').length}
                    </div>
                    <div>Active</div>
                  </div>
                  <div style={{ 
                    background: '#fff8e1', 
                    padding: '10px', 
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {importData.filter(p => p.Status === 'Vacant').length}
                    </div>
                    <div>Vacant</div>
                  </div>
                  <div style={{ 
                    background: '#f5f5f5', 
                    padding: '10px', 
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {importData.filter(p => p['Reporting To Position Title'] && p['Reporting To Position Title'].trim() !== '').length}
                    </div>
                    <div>With Reporting To</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {duplicatePositions.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <NoticeBox warning title={`${duplicatePositions.length} duplicate position(s) detected`}>
              <p>These positions already exist in the system:</p>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Position ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Existing TEI</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {duplicatePositions.map((dup, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{dup.positionId || 'N/A'}</TableCell>
                      <TableCell>{dup['Position Title']}</TableCell>
                      <TableCell>{dup.Department}</TableCell>
                      <TableCell>{dup.duplicateTei}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div style={{ marginTop: '12px' }}>
                <label><strong>How should duplicates be handled?</strong></label>
                <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                  <Button
                    secondary={duplicateHandling !== 'skip'}
                    onClick={() => setDuplicateHandling('skip')}
                  >
                    Skip Duplicates
                  </Button>
                  <Button
                    secondary={duplicateHandling !== 'update'}
                    onClick={() => setDuplicateHandling('update')}
                  >
                    Update Existing
                  </Button>
                  <Button
                    secondary={duplicateHandling !== 'abort'}
                    onClick={() => setDuplicateHandling('abort')}
                  >
                    Abort Import
                  </Button>
                </div>
              </div>
            </NoticeBox>
          </div>
        )}

        {/* Step 3: Import Progress */}
        {activeStep === 3 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {importStatus.loading ? (
              <div>
                <CircularLoader size="48px" />
                <h3 style={{ marginTop: '20px' }}>Importing Positions...</h3>
                <p style={{ marginTop: '10px' }}>
                  Processed {importStatus.processed} of {importStatus.total} positions
                  ({Math.round((importStatus.processed / importStatus.total) * 100)}%)
                </p>
                <div style={{ 
                  width: '100%', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '4px',
                  margin: '30px 0 20px',
                  height: '20px'
                }}>
                  <div 
                    style={{ 
                      width: `${(importStatus.processed / importStatus.total) * 100}%`,
                      height: '100%',
                      backgroundColor: '#2C6693',
                      borderRadius: '4px',
                      transition: 'width 0.5s'
                    }}
                  />
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '40px',
                  marginTop: '20px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>
                      ✓ {importStatus.success}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Success</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f44336' }}>
                      ✗ {importStatus.failed}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ 
                  fontSize: '72px', 
                  color: importStatus.failed === 0 ? '#4caf50' : '#ff9800',
                  marginBottom: '20px'
                }}>
                  {importStatus.failed === 0 ? '✓' : '⚠'}
                </div>
                
                <NoticeBox 
                  title={importStatus.failed === 0 ? "Import Complete!" : "Import Completed with Issues"}
                  valid={importStatus.failed === 0}
                  warning={importStatus.failed > 0}
                >
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                    Successfully imported <strong>{importStatus.success}</strong> positions.
                    {importStatus.failed > 0 && (
                      <span> <strong>{importStatus.failed}</strong> positions failed to import.</span>
                    )}
                  </p>
                  {importStatus.failed > 0 && (
                    <p style={{ fontSize: '14px', color: '#666' }}>
                      Check the error log for details on failed imports.
                    </p>
                  )}
                </NoticeBox>
                
                <div style={{ marginTop: '30px' }}>
                  <Button 
                    onClick={() => {
                      handleReset();
                      onClose();
                    }}
                    primary
                  >
                    Close
                  </Button>
                  {importStatus.failed > 0 && (
                    <Button 
                      onClick={() => {
                        // Option to retry failed imports
                        alert('Retry failed imports feature coming soon');
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      Retry Failed
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </ModalContent>
      
      <ModalActions>
        <button 
        style={{
              backgroundColor: 'rgb(196, 109, 10)',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
              cursor: 'pointer',
              marginRight: 10,
              }}
        onClick={activeStep === 1 ? onClose : handleReset}>
          {activeStep === 1 ? 'Close' : 'Start Over'}
        </button>
        
        {activeStep === 2 && importData.length > 0 && (
          <Button 
            primary 
            onClick={handleStartImport}
            disabled={importStatus.loading}
          >
            Import {importData.length} Positions
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default BulkPositionImportDialog;